import { useMemo } from "react";
import moment from "moment";
import type { Event } from "../../types/Event";
import type { Resource } from "../../types/Resource";

const isOverlappingByTime = (a: Event, b: Event) => {
    const aStart = moment(a.startDate);
    const aEnd = moment(a.endDate || a.startDate);

    const bStart = moment(b.startDate);
    const bEnd = moment(b.endDate || b.startDate);

    return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
};

const isOverlappingByDate = (a: Event, b: Event) => {
    const aStart = moment(a.startDate).startOf("day");
    const aEnd = moment(a.endDate || a.startDate).endOf("day");

    const bStart = moment(b.startDate).startOf("day");
    const bEnd = moment(b.endDate || b.startDate).endOf("day");

    return aStart.isSameOrBefore(bEnd) && aEnd.isSameOrAfter(bStart);
};

const calculateLayout = (events: Event[], resources: Partial<Resource>[], mode?: string) => {
    // Stores the max lanes needed for each resource
    const resourceLayouts: { [key: string]: { totalLanes: number, type?: string } } = {};
    // Stores the calculated lane for each event
    const eventLayouts: { [key: string]: { laneIndex: number } } = {};

    if (!events || !resources) {
        return { resourceLayouts, eventLayouts };
    }

    // Choose overlap strategy
    const overlapFn = mode === "day" ? isOverlappingByTime : isOverlappingByDate;

    resources.forEach((resource) => {
        // 1. Get all events for the current resource and sort them by start date/time.
        // Unavailable events are rendered as a full-row overlay, so they don't take a lane.
        const resourceEvents = events
            .filter((e) => {
                if (e.resourceId !== resource.id) return false;
                if ((e as any).type === "unavailable") {
                    eventLayouts[e.id] = { laneIndex: 0 };
                    return false;
                }
                return true;
            })
            .sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());

        if (resourceEvents.length === 0) {
            resourceLayouts[resource.id as string] = { totalLanes: 1, type: (resource as any)?.type }; // Default to 1 lane
            return;
        }

        // 2. This array holds the lanes. Each lane is an array of events.
        const lanes: Event[][] = [];

        resourceEvents.forEach((event) => {
            let placed = false;

            // 3. Try to place the event in an existing lane
            for (let i = 0; i < lanes.length; i++) {
                const overlap = lanes[i].some((laneEvent) => overlapFn(event, laneEvent));

                if (!overlap) {
                    lanes[i].push(event);
                    eventLayouts[event.id] = { laneIndex: i };
                    placed = true;
                    break;
                }
            }

            // 4. If no existing lane was found, create a new one
            if (!placed) {
                lanes.push([event]);
                eventLayouts[event.id] = { laneIndex: lanes.length - 1 };
            }
        });

        // 5. The total number of lanes is the max needed for this resource
        resourceLayouts[resource.id as string] = {
            totalLanes: Math.max(1, lanes.length), // Ensure at least 1 lane
        };
    });

    return { resourceLayouts, eventLayouts };
};

export const useEventLayout = (
    events: Event[],
    resources: Partial<Resource>[],
    mode?: string,
) => {
    return useMemo(() => calculateLayout(events, resources, mode), [events, resources, mode]);
};
